import { useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import ChangePasswordDialog from "@/components/common/ChangePasswordDialog";
import {
  Truck, Scales, ClipboardText,
  Package, Factory, SignOut, Gauge, Buildings, Drop, UsersThree,
  List, ShieldCheck, Flask, Key, ChartBar, ClockClockwise, Database,
} from "@phosphor-icons/react";

const NAV = [
  { section: "Beranda", items: [
    { to: "/", label: "Dashboard", icon: Gauge, testid: "nav-dashboard",
      roles: ["admin", "owner", "admin_kantor", "admin_pabrik", "inventory", "security", "supervisor", "approver", "lab"] },
    { to: "/stock", label: "Ringkasan Stok", icon: ChartBar, testid: "nav-stock",
      roles: ["admin", "owner", "admin_kantor", "admin_pabrik", "inventory"] },
  ]},
  { section: "Persetujuan", items: [
    { to: "/approval", label: "Pusat Persetujuan", icon: ShieldCheck, testid: "nav-approval",
      roles: ["admin", "owner", "admin_kantor", "approver"] },
    { to: "/approval-history", label: "Riwayat Persetujuan", icon: ClipboardText, testid: "nav-approval-history",
      roles: ["admin", "owner", "admin_kantor", "approver"] },
  ]},
  { section: "Operasional (Alur Lapangan)", items: [
    { to: "/gerbang", label: "1. Gerbang Masuk / Keluar", icon: Truck, testid: "nav-gerbang",
      roles: ["admin", "owner", "admin_kantor", "admin_pabrik", "security", "supervisor"] },
    { to: "/weigh-in", label: "2. Timbang Masuk", icon: Scales, testid: "nav-weigh-in",
      roles: ["admin", "owner", "admin_kantor", "admin_pabrik", "security", "supervisor"] },
    { to: "/receiving", label: "3. Penerimaan (Receiving)", icon: Package, testid: "nav-receiving",
      roles: ["admin", "owner", "admin_kantor", "admin_pabrik", "supervisor", "lab"] },
    { to: "/loading", label: "3b. Proses Loading", icon: Factory, testid: "nav-loading",
      roles: ["admin", "owner", "admin_kantor", "admin_pabrik", "supervisor"] },
    { to: "/weigh-out", label: "4. Timbang Keluar", icon: Scales, testid: "nav-weigh-out",
      roles: ["admin", "owner", "admin_kantor", "admin_pabrik", "security", "supervisor"] },
    { to: "/log-kendaraan", label: "5. Log Kendaraan", icon: ClipboardText, testid: "nav-log-kendaraan",
      roles: ["admin", "owner", "admin_kantor", "admin_pabrik", "inventory", "security", "supervisor", "approver", "lab"] },
  ]},
  { section: "Delivery Order", items: [
    { to: "/delivery", label: "Pengiriman (Delivery)", icon: Buildings, testid: "nav-delivery",
      roles: ["admin", "owner", "admin_kantor", "admin_pabrik", "supervisor"] },
    { to: "/delivery/timeline", label: "Timeline DO", icon: ClockClockwise, testid: "nav-delivery-timeline",
      roles: ["admin", "owner", "admin_kantor", "supervisor"] },
  ]},
  { section: "Inventory", items: [
    { to: "/tank-stock", label: "Stok Tangki (Real-time)", icon: Drop, testid: "nav-tank-stock",
      roles: ["admin", "owner", "admin_kantor", "admin_pabrik", "inventory", "supervisor"] },
    { to: "/opname", label: "Stok Opname Harian", icon: Drop, testid: "nav-opname",
      roles: ["admin", "owner", "admin_kantor", "inventory", "supervisor"] },
  ]},
  { section: "Laporan", items: [
    { to: "/reports/daily", label: "Laporan Harian", icon: ChartBar, testid: "nav-report-daily",
      roles: ["admin", "owner", "admin_kantor", "inventory"] },
    { to: "/reports/period", label: "Laporan Periodik", icon: ChartBar, testid: "nav-report-period",
      roles: ["admin", "owner", "admin_kantor"] },
    { to: "/receiving/timeline", label: "Timeline Receiving", icon: ClockClockwise, testid: "nav-receiving-timeline",
      roles: ["admin", "owner", "admin_kantor", "supervisor", "inventory"] },
    { to: "/history", label: "Riwayat Semua Data", icon: ClipboardText, testid: "nav-history",
      roles: ["admin", "owner", "admin_kantor"] },
  ]},
  { section: "Administrasi", items: [
    { to: "/customers", label: "Data Customer", icon: Buildings, testid: "nav-customers",
      roles: ["admin", "owner", "admin_kantor", "admin_pabrik"] },
    { to: "/users", label: "Manajemen User", icon: UsersThree, testid: "nav-users",
      roles: ["admin", "owner"] },
    { to: "/edit-logs", label: "Riwayat Perubahan Data", icon: ClipboardText, testid: "nav-edit-logs",
      roles: ["admin", "owner"] },
    { to: "/admin/storage", label: "Storage Maintenance", icon: Database, testid: "nav-storage",
      roles: ["admin", "owner"] },
  ]},
];

function SidebarContent({ onNavigate }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [pwOpen, setPwOpen] = useState(false);
  const doLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="w-full bg-slate-950 text-slate-100 h-full flex flex-col">
      <div className="px-4 py-4 border-b border-slate-800/70">
        <div className="flex items-center gap-3">
          <img src="/primergy-logo.jpeg" alt="Primergy" className="w-11 h-11 rounded-full bg-white object-contain shrink-0 shadow-md" />
          <div className="leading-tight min-w-0">
            <div className="font-heading font-bold text-[14px] tracking-tight truncate">PT. PRIMERGY</div>
            <div className="text-[9.5px] uppercase tracking-[0.18em] text-sky-300">Solution</div>
          </div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-5">
        {NAV.map((s) => {
          const items = s.items.filter((it) => !it.roles || it.roles.includes(user?.role));
          if (items.length === 0) return null;
          return (
            <div key={s.section}>
              <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 px-3 mb-1.5">
                {s.section}
              </div>
              <div className="space-y-0.5">
                {items.map((it) => (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    end={it.to === "/"}
                    data-testid={it.testid}
                    onClick={() => onNavigate && onNavigate()}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 rounded-md text-[14px] transition-colors duration-200 ${
                        isActive
                          ? "bg-slate-800 text-sky-400 font-semibold"
                          : "text-slate-300 hover:bg-slate-800/60 hover:text-white"
                      }`
                    }
                  >
                    <it.icon size={18} weight="duotone" />
                    <span className="truncate">{it.label}</span>
                  </NavLink>
                ))}
              </div>
            </div>
          );
        })}
      </nav>

      <div className="border-t border-slate-800/70 p-3 space-y-2">
        <div className="flex items-center gap-3 px-2">
          <div className="w-9 h-9 rounded-full bg-sky-500/20 border border-sky-500/40 flex items-center justify-center text-sky-300 font-heading text-sm shrink-0">
            {(user?.name || user?.email || "U").charAt(0).toUpperCase()}
          </div>
          <div className="leading-tight min-w-0 flex-1">
            <div className="text-[13px] font-semibold truncate">{user?.name || "User"}</div>
            <div className="text-[10px] text-slate-400 truncate uppercase tracking-wide">{user?.role}</div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button
            data-testid="sidebar-change-password"
            onClick={() => setPwOpen(true)}
            className="flex items-center justify-center gap-1.5 py-2 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-200 text-[12px] font-medium transition-colors duration-200"
          >
            <Key size={13} weight="bold" /> Password
          </button>
          <button
            data-testid="logout-btn"
            onClick={doLogout}
            className="flex items-center justify-center gap-1.5 py-2 rounded-md bg-red-600/20 hover:bg-red-600/30 text-red-200 border border-red-500/30 text-[12px] font-medium transition-colors duration-200"
          >
            <SignOut size={13} weight="bold" /> Keluar
          </button>
        </div>
      </div>

      <ChangePasswordDialog open={pwOpen} onClose={() => setPwOpen(false)} />
    </div>
  );
}

export default function Sidebar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <>
      {/* Desktop */}
      <aside className="hidden lg:block w-64 shrink-0 sticky top-0 h-screen">
        <SidebarContent />
      </aside>

      {/* Mobile drawer trigger + sheet */}
      <Sheet open={open} onOpenChange={setOpen} key={location.pathname}>
        <SheetTrigger asChild>
          <button
            data-testid="mobile-menu-btn"
            className="lg:hidden fixed top-3 left-3 z-40 w-10 h-10 rounded-md bg-slate-950 text-white flex items-center justify-center shadow-lg"
            aria-label="Buka menu"
          >
            <List size={20} weight="bold" />
          </button>
        </SheetTrigger>
        <SheetContent side="left" className="p-0 w-72 bg-slate-950 border-slate-800 [&>button]:text-slate-400 [&>button]:top-4 [&>button]:right-4">
          <SidebarContent onNavigate={() => setOpen(false)} />
        </SheetContent>
      </Sheet>
    </>
  );
}
